import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { getAuthConfig } from '../config/auth-config';
import { getAwsCredentials, clearCachedCredentials } from './aws-credentials';
import { debugLog, debugError } from '../lib/stores/debug';

function isCredentialError(e: any): boolean {
  const msg = e?.message?.toLowerCase() ?? '';
  return msg.includes('credential') || msg.includes('signature') || msg.includes('token');
}

async function signAndFetch(
  url: URL,
  method: string,
  bodyStr: string | undefined,
  region: string,
): Promise<Response> {
  const credentials = await getAwsCredentials();

  const request = new HttpRequest({
    method,
    protocol: url.protocol,
    hostname: url.hostname,
    path: url.pathname,
    query: Object.fromEntries(url.searchParams),
    headers: {
      'content-type': 'application/json',
      'host': url.hostname,
    },
    body: bodyStr,
  });

  const signer = new SignatureV4({
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    region,
    service: 'execute-api',
    sha256: Sha256,
  });

  const signed = await signer.sign(request) as HttpRequest;

  const fetchHeaders: Record<string, string> = {};
  for (const [key, value] of Object.entries(signed.headers)) {
    if (key.toLowerCase() !== 'host') {
      fetchHeaders[key] = value;
    }
  }

  return fetch(url.toString(), {
    method: signed.method,
    headers: fetchHeaders,
    body: bodyStr,
  });
}

export async function invokeApiGateway<T = any>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any
): Promise<T> {
  const config = getAuthConfig();
  const baseUrl = config.apiGatewayUrl.replace(/\/+$/, '');
  const url = new URL(`${baseUrl}${path}`);
  const bodyStr = body ? JSON.stringify(body) : undefined;

  debugLog('API', `${method} ${url.toString()}`);
  if (bodyStr) debugLog('API', 'Body:', JSON.parse(bodyStr));

  const startTime = Date.now();
  try {
    const response = await signAndFetch(url, method, bodyStr, config.region);

    if (!response.ok) {
      const errorText = await response.text();
      debugError('API', `${method} ${url.pathname} → ${response.status}`, errorText);
      throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    debugLog('API', `${method} ${url.pathname} → ${response.status} (${Date.now() - startTime}ms)`);
    return response.json();
  } catch (e: any) {
    if (isCredentialError(e)) {
      clearCachedCredentials();
      // Retry once with fresh credentials
      const response = await signAndFetch(url, method, bodyStr, config.region);

      if (!response.ok) {
        const errorText = await response.text();
        debugError('API', `${method} ${url.pathname} → ${response.status} (retry)`, errorText);
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      debugLog('API', `${method} ${url.pathname} → ${response.status} (${Date.now() - startTime}ms, retried)`);
      return response.json();
    }
    throw e;
  }
}
